import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../lib/auth-context';

export function LoginPage() {
  const { t } = useTranslation('common');
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError(t('login.errors.emailRequired'));
      return;
    }
    if (!password.trim()) {
      setError(t('login.errors.passwordRequired'));
      return;
    }

    setLoading(true);
    try {
      const success = await login(email, password);
      if (!success) {
        setError(t('login.errors.invalidCredentials'));
      }
      // Success -> AuthContext sets user -> App.tsx re-renders and navigates away
    } catch (err) {
      if (err instanceof Error && err.message) {
        setError(err.message);
      } else {
        setError('Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        {/* Masthead — mono eyebrow + hairline, azure shield mark, Plex Sans wordmark */}
        <div className="mb-8">
          <div className="flex items-center gap-2.5 mb-5">
            <span className="font-mono text-[0.6875rem] uppercase tracking-[0.18em] text-ink-muted">
              Backoffice
            </span>
            <span className="h-px flex-1 bg-hairline-strong" aria-hidden="true" />
          </div>
          <div className="flex items-center gap-3">
            <span
              className="inline-flex items-center justify-center w-11 h-11 rounded-sm bg-primary text-white flex-shrink-0"
              aria-hidden="true"
            >
              <svg width="24" height="24" viewBox="0 0 36 36" fill="none">
                <path d="M11 25.5V10.5L25 25.5V10.5" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            <h1 className="font-display text-3xl text-ink leading-none">{t('login.title')}</h1>
          </div>
          <p className="text-sm text-ink-muted mt-3">{t('login.subtitle')}</p>
        </div>

        {/* Login form */}
        <div className="bg-surface-raised rounded-sm border border-hairline p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block font-mono text-[0.6875rem] font-medium uppercase tracking-[0.08em] text-ink-muted mb-1.5">
                {t('login.emailLabel')}
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="w-full px-3 py-2.5 bg-white border border-hairline-strong rounded-sm text-sm font-mono text-ink placeholder:text-ink-muted placeholder:font-sans focus:outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary"
                placeholder="you@company.com"
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="password" className="block font-mono text-[0.6875rem] font-medium uppercase tracking-[0.08em] text-ink-muted mb-1.5">
                {t('login.passwordLabel')}
              </label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full px-3 py-2.5 bg-white border border-hairline-strong rounded-sm text-sm font-mono text-ink placeholder:text-ink-muted placeholder:font-sans focus:outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary"
                placeholder="Enter your password"
                disabled={loading}
              />
            </div>

            {error && (
              <div className="text-sm text-danger bg-danger-tint border border-danger/20 rounded-sm px-3 py-2" role="alert">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 bg-primary text-white rounded-sm text-sm font-semibold hover:bg-primary-hover transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-raised"
            >
              {loading ? (
                <span className="inline-flex items-center gap-2">
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="32" strokeDashoffset="10" strokeLinecap="round" />
                  </svg>
                  {t('login.signingIn')}
                </span>
              ) : (
                t('login.signIn')
              )}
            </button>
          </form>
        </div>

        {/* Sign-in notice */}
        <div className="mt-6 px-3 py-3 bg-surface-sunken border border-hairline rounded-sm">
          <p className="text-xs text-ink-muted text-center">
            {t('login.ssoHint')}
          </p>
        </div>
      </div>
    </div>
  );
}
